import React, { useEffect, useState, useCallback } from 'react';
import { WorkflowStep } from '../../../types/actionConfig';
import styles from './WorkflowStepsDisplay.module.css';

interface WorkflowStepsDisplayProps {
  artifactId: string;
  steps: WorkflowStep[];
  onStepAction?: (step: WorkflowStep) => void;
  onStepsValidated?: (allComplete: boolean) => void;
  className?: string;
}

/**
 * Display and manage workflow steps for artifact development
 * Used in architect mode to track state machine creation progress
 */
export const WorkflowStepsDisplay: React.FC<WorkflowStepsDisplayProps> = ({
  artifactId,
  steps: initialSteps,
  onStepAction,
  onStepsValidated,
  className = '',
}) => {
  const [steps, setSteps] = useState<WorkflowStep[]>(initialSteps);
  const [validating, setValidating] = useState<Record<string, boolean>>({});

  // Validate step completion status
  const validateStep = useCallback(async (step: WorkflowStep): Promise<boolean> => {
    if (!step.validationEndpoint) {
      return step.isComplete;
    }

    setValidating(prev => ({ ...prev, [step.id]: true }));

    try {
      const response = await fetch(
        step.validationEndpoint.replace('{artifactId}', artifactId)
      );

      if (!response.ok) {
        console.error(`[WorkflowSteps] Validation failed for ${step.id}`);
        return false;
      }

      const result = await response.json();
      return result.isComplete || result.count > 0 || false;
    } catch (error) {
      console.error(`[WorkflowSteps] Error validating ${step.id}:`, error);
      return false;
    } finally {
      setValidating(prev => ({ ...prev, [step.id]: false }));
    }
  }, [artifactId]);

  // Validate all steps on mount and when artifact changes
  useEffect(() => {
    const validateAllSteps = async () => {
      const validatedSteps = await Promise.all(
        steps.map(async step => {
          const isComplete = await validateStep(step);
          return { ...step, isComplete };
        })
      );

      setSteps(validatedSteps);

      // Notify parent of validation status
      const allComplete = validatedSteps
        .filter(s => s.isRequired)
        .every(s => s.isComplete);
      onStepsValidated?.(allComplete);
    };

    validateAllSteps();
  }, [artifactId]); // Only re-run when artifact changes, not on every step validation

  const handleStepClick = (step: WorkflowStep) => {
    if (step.isComplete) return;
    onStepAction?.(step);
  };

  const getStepIcon = (step: WorkflowStep) => {
    if (validating[step.id]) {
      return <span className={`${styles.stepIcon} ${styles.validating}`}>⏳</span>;
    }
    if (step.isComplete) {
      return <span className={`${styles.stepIcon} ${styles.complete}`}>✓</span>;
    }
    if (step.isRequired) {
      return <span className={`${styles.stepIcon} ${styles.required}`}>⚠</span>;
    }
    return <span className={`${styles.stepIcon} ${styles.optional}`}>○</span>;
  };

  const requiredComplete = steps.filter(s => s.isRequired && s.isComplete).length;
  const requiredTotal = steps.filter(s => s.isRequired).length;
  const allRequiredComplete = requiredComplete === requiredTotal;

  return (
    <div className={`${styles.workflowStepsDisplay} ${className}`}>
      <div className={styles.workflowHeader}>
        <h4>Development Workflow</h4>
        <div className={`${styles.workflowProgress} ${allRequiredComplete ? styles.complete : ''}`}>
          {requiredComplete}/{requiredTotal} Required Steps Complete
        </div>
      </div>

      <div className={styles.workflowSteps}>
        {steps.map(step => (
          <div
            key={step.id}
            className={`${styles.workflowStep} ${step.isComplete ? styles.complete : ''} ${
              step.isRequired ? styles.required : styles.optional
            } ${step.actionEndpoint && !step.isComplete ? styles.clickable : ''}`}
            onClick={() => handleStepClick(step)}
          >
            <div className={styles.stepStatus}>
              {getStepIcon(step)}
            </div>
            <div className={styles.stepContent}>
              <div className={styles.stepLabel}>
                {step.label}
                {step.isRequired && !step.isComplete && (
                  <span className={styles.requiredBadge}>Required</span>
                )}
              </div>
              {step.description && (
                <div className={styles.stepDescription}>{step.description}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {allRequiredComplete && (
        <div className={styles.workflowCompleteMessage}>
          ✓ All required steps complete! Ready to proceed.
        </div>
      )}
    </div>
  );
};

